import React, { useEffect, useMemo, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, EmptyState, Chip } from "../components/Common";
import { Users, Bed, Stethoscope, Activity, Bell, Calendar, Megaphone, AlertTriangle, Settings, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { WardManagerDialog } from "./admitted/WardManagerDialog";
import { DutyRosterCard } from "./admitted/DutyRosterCard";
import { StaffManagerCard } from "./admitted/StaffManagerCard";

const BED_TONE = {
  vacant: "bg-white text-slate-600 border-slate-200",
  occupied: "bg-[#1976D2] text-white border-[#1565C0]",
  cleaning: "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]",
  maintenance: "bg-slate-200 text-slate-500 border-slate-300",
  reserved: "bg-[#e6f4ea] text-[#1B5E20] border-[#c9e5d1]",
};

export default function HospitalAdmin() {
  const [stats, setStats] = useState({});
  const [trend, setTrend] = useState([]);
  const [ops, setOps] = useState(null);
  const [bedsData, setBedsData] = useState({ beds: [], wards: {} });
  const [activeBed, setActiveBed] = useState(null);
  const [showWardManager, setShowWardManager] = useState(false);
  const [drill, setDrill] = useState(null);
  const scrollTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);

  const load = async () => {
    const [s, t, o, b] = await Promise.all([
      api.get("/dashboard/stats"), api.get("/dashboard/revenue-trend"),
      api.get("/admin/ops-dashboard"), api.get("/beds"),
    ]);
    setStats(s.data); setTrend(t.data || []); setOps(o.data); setBedsData(b.data);
  };
  useEffect(() => { load(); }, []);

  if (!ops) return <div className="p-8 text-slate-500">Loading operations dashboard…</div>;

  const setBedStatus = async (bed_id, status, patient_id = null) => {
    try {
      await api.post("/beds/status", { bed_id, status, patient_id });
      toast.success(`Bed marked ${status}`);
      setActiveBed(null);
      load();
    } catch { toast.error("Failed to update bed"); }
  };

  const wardNames = Object.keys(bedsData.wards);
  const totalBeds = bedsData.beds.length;
  const occupied = bedsData.beds.filter((b) => b.status === "occupied").length;
  const pct = totalBeds ? Math.round((occupied / totalBeds) * 100) : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="hospital-admin-dashboard">
      <PageHeader title="Hospital Administrator" subtitle="Facility-wide operations, staff, occupancy and alerts." />

      {drill && <AdminDrillModal scope={drill} ops={ops} bedsData={bedsData} stats={stats} onClose={() => setDrill(null)} />}

      {/* Patient Operations */}
      <div>
        <div className="section-heading mb-3">Patient operations</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="k-outpatients" onClick={() => setDrill("outpatients")} label="Outpatients today" value={ops.outpatients_today} icon={Users} tone="blue" hint="Click for list" />
          <KpiCard testId="k-inpatients" onClick={() => setDrill("inpatients")} label="Inpatients (live)" value={occupied} icon={Bed} tone="slate" hint="Click for admitted list" />
          <KpiCard testId="k-admissions" onClick={() => setDrill("admissions")} label="Admissions today" value={ops.admissions_today} icon={Users} tone="green" hint="Click for today's admits" />
          <KpiCard testId="k-occupancy" onClick={() => { setDrill(null); scrollTo("bed-board-anchor"); }} label="Bed occupancy" value={`${pct}%`} icon={Bed} tone={pct > 85 ? "red" : "blue"} hint={`${occupied}/${totalBeds} · click for Bed board`} />
          <KpiCard testId="k-ed" onClick={() => setDrill("ed")} label="ED visits" value={ops.ed_visits} tone="amber" hint="Click for ED queue" />
          <KpiCard testId="k-icu" onClick={() => setDrill("icu")} label="ICU used" value={bedsData.wards?.ICU?.occupied || 0} tone="red" hint={`of ${bedsData.wards?.ICU?.total || 0} · click for beds`} />
          <KpiCard testId="k-theatre" onClick={() => setDrill("theatre")} label="Theatre cases today" value={ops.theatre_cases_today} tone="blue" hint="Click for cases" />
          <KpiCard testId="k-revenue" onClick={() => setDrill("revenue")} label="Revenue today" value={UGX(stats.revenue_today)} tone="green" hint="Click for payments" />
        </div>
      </div>

      {/* Staff Operations */}
      <div>
        <div className="section-heading mb-3">Staff operations · {ops.staff.current_shift}</div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard testId="k-doctors" onClick={() => setDrill("doctors")} label="Doctors on duty" value={ops.staff.doctors_on_duty} icon={Stethoscope} tone="blue" hint="Click for names" />
          <KpiCard testId="k-nurses" onClick={() => setDrill("nurses")} label="Nurses on duty" value={ops.staff.nurses_on_duty} icon={Stethoscope} tone="blue" hint="Click for names" />
          <KpiCard testId="k-leave" onClick={() => setDrill("onleave")} label="On leave" value={ops.staff.on_leave} tone="amber" hint="Click for list" />
          <KpiCard testId="k-absent" onClick={() => setDrill("absent")} label="Absent" value={ops.staff.absent} tone="red" hint="Click for list" />
          <KpiCard testId="k-late" onClick={() => setDrill("late")} label="Late" value={ops.staff.late} tone="amber" hint="Click for list" />
        </div>
      </div>

      <StaffManagerCard />

      <DutyRosterCard />

      <SectionCard id="bed-board-anchor" title="Bed & ward board" actions={
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[11px]">
            {Object.entries(BED_TONE).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded-sm border ${v}`}></span><span className="text-slate-600 capitalize">{k}</span></span>
            ))}
          </div>
          <button
            data-testid="ward-manager-open"
            onClick={() => setShowWardManager(true)}
            className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5"
          ><Settings className="w-3.5 h-3.5" /> Manage wards</button>
        </div>
      }>
        <div className="p-5 space-y-5" data-testid="bed-board">
          {wardNames.map((w) => (
            <div key={w}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold flex items-center gap-2">{w} <Chip tone={bedsData.wards[w].occupied === bedsData.wards[w].total ? "red" : "slate"}>{bedsData.wards[w].occupied}/{bedsData.wards[w].total}</Chip></div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid={`ward-${w.toLowerCase()}`}>
                {bedsData.beds.filter((b) => b.ward === w).map((b) => (
                  <button key={b.bed_id} data-testid={`bed-${b.bed_no}`} onClick={() => setActiveBed(b)} className={`px-3 py-2 rounded-lg border text-xs font-medium min-w-[75px] text-center transition-transform hover:-translate-y-0.5 ${BED_TONE[b.status]}`} title={b.patient_name || b.status}>
                    <div className="tabular text-[13px] font-bold">{b.bed_no}</div>
                    {b.status === "occupied" ? <div className="text-[10px] truncate max-w-[70px] mt-0.5 opacity-90">{b.patient_name?.split(" ")[0]}</div> : <div className="text-[10px] mt-0.5 capitalize opacity-70">{b.status}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Hospital alerts">
          <ul className="divide-y divide-slate-100">
            {ops.alerts.map((a, i) => (
              <li key={i} className="p-3 flex items-start gap-2">
                <Chip tone={a.severity === "critical" ? "red" : a.severity === "warning" ? "amber" : "blue"}>{a.severity}</Chip>
                <div className="text-sm text-slate-800">{a.message}</div>
              </li>
            ))}
          </ul>
        </SectionCard>
        <SectionCard title="Appointments today">
          <div className="p-4 space-y-3 text-sm">
            <RowStat label="Booked" value={ops.appointments.today} icon={Calendar} />
            <RowStat label="Completed" value={ops.appointments.completed} />
            <RowStat label="Missed" value={ops.appointments.missed} tone="red" />
            <RowStat label="Upcoming" value={ops.appointments.upcoming} />
          </div>
        </SectionCard>
        <SectionCard title="Announcements">
          <ul className="divide-y divide-slate-100">
            {ops.announcements.map((n, i) => (
              <li key={i} className="p-3">
                <div className="flex items-center gap-2"><Megaphone className="w-3.5 h-3.5 text-slate-400" /><div className="text-sm font-medium">{n.title}</div></div>
                <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
                <div className="text-[11px] text-slate-400 mt-1">{n.date}</div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <SectionCard title="Revenue trend · last 14 days">
        <div className="p-4 h-64">
          <ResponsiveContainer><LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => UGX(v)} />
            <Line type="monotone" dataKey="revenue" stroke="#2E7D32" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart></ResponsiveContainer>
        </div>
      </SectionCard>

      {activeBed && <BedModal bed={activeBed} onClose={() => setActiveBed(null)} onSet={setBedStatus} />}
      {showWardManager && (
        <WardManagerDialog
          bedsData={bedsData}
          onClose={() => setShowWardManager(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function BedModal({ bed, onClose, onSet }) {
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="bed-modal">
        <div className="mb-3">
          <div className="text-xs text-slate-500">{bed.ward} ward</div>
          <div className="text-lg font-semibold tabular">Bed {bed.bed_no}</div>
          {bed.patient_name && <div className="text-sm text-slate-600 mt-1">Currently: <span className="font-medium">{bed.patient_name}</span></div>}
        </div>
        <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Change status</div>
        <div className="grid grid-cols-2 gap-2">
          {["vacant", "cleaning", "maintenance", "reserved"].map((s) => (
            <button key={s} data-testid={`set-bed-${s}`} onClick={() => onSet(bed.bed_id, s)} className={`h-10 rounded-lg text-sm font-medium border ${BED_TONE[s]}`}>{s}</button>
          ))}
        </div>
        <button onClick={onClose} className="mt-3 w-full h-10 rounded-lg border border-slate-300 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function RowStat({ label, value, icon: Icon, tone }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span>
      <span className={`tabular font-semibold ${tone === "red" ? "text-[#B71C1C]" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

const DRILL_META = {
  outpatients: { title: "Outpatients today", endpoint: "/queue", filter: (r) => (r.visit_type || "").toLowerCase().includes("outpatient") || (r.department || "").toLowerCase() !== "emergency", cols: [["Time","created_at","time"],["Patient","patient_name"],["Patient ID","patient_ths_id","tab"],["Department","department"],["Doctor","assigned_doctor_name"],["Status","status","pill"]] },
  inpatients:  { title: "Inpatients (live)", endpoint: "/admissions?status=Admitted", cols: [["Patient","patient_name"],["Patient ID","patient_ths_id","tab"],["Ward","ward"],["Bed","bed"],["Doctor","attending_doctor_name"],["Admitted","admitted_at","time"]] },
  admissions:  { title: "Admissions today", endpoint: "/admissions?today=1", cols: [["Time","admitted_at","time"],["Patient","patient_name"],["Patient ID","patient_ths_id","tab"],["Ward","ward"],["Bed","bed"],["Reason","admission_reason"]] },
  ed:          { title: "ED visits today", endpoint: "/queue", filter: (r) => (r.department || "").toLowerCase() === "emergency" || (r.visit_type || "").toLowerCase().includes("emergency"), cols: [["Arrival","created_at","time"],["Patient","patient_name"],["Patient ID","patient_ths_id","tab"],["Triage","priority","pill"],["Status","status","pill"]] },
  theatre:     { title: "Theatre cases today", endpoint: "/theatre/cases?today=1", cols: [["Time","scheduled_at","time"],["Patient","patient_name"],["Procedure","procedure"],["Surgeon","surgeon_name"],["Status","status","pill"]] },
  revenue:     { title: "Revenue today · payments", endpoint: "/payments", filter: (p) => (p.created_at || "").slice(0,10) === new Date().toISOString().slice(0,10), cols: [["Time","created_at","time"],["Patient","patient_name"],["Invoice","invoice_no","tab"],["Method","method"],["Amount","amount","ugx"]] },
  doctors:     { title: "Doctors on duty", endpoint: "/staff?role=doctor", cols: [["Name","name"],["Specialty","specialty"],["Availability","availability","pill"],["Email","email","tab"]] },
  nurses:      { title: "Nurses on duty", endpoint: "/staff?role=nurse", cols: [["Name","name"],["Department","department"],["Email","email","tab"]] },
  onleave:     { title: "On leave today", static: (ops) => ops.staff?.on_leave_list || [], cols: [["Name","name"],["Role","role"],["From","from","time"],["To","to","time"]] },
  absent:      { title: "Absent today", static: (ops) => ops.staff?.absent_list || [], cols: [["Name","name"],["Role","role"]] },
  late:        { title: "Late today", static: (ops) => ops.staff?.late_list || [], cols: [["Name","name"],["Role","role"],["Expected","expected_start","time"],["Actual","actual_start","time"]] },
};

function AdminDrillModal({ scope, ops, bedsData, stats, onClose }) {
  const [rows, setRows] = useState(null);
  const meta = DRILL_META[scope];
  const isIcu = scope === "icu";

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (isIcu) { setRows((bedsData.beds || []).filter((b) => b.ward === "ICU")); return; }
      if (!meta) return;
      if (meta.static) { setRows(meta.static(ops) || []); return; }
      try {
        const { data } = await api.get(meta.endpoint);
        const list = meta.filter ? (data || []).filter(meta.filter) : (data || []);
        if (!cancel) setRows(list);
      } catch { if (!cancel) setRows([]); }
    })();
    return () => { cancel = true; };
  }, [scope]);

  if (!meta && !isIcu) return null;
  const title = isIcu ? `ICU beds occupied · ${bedsData.wards?.ICU?.occupied || 0} of ${bedsData.wards?.ICU?.total || 0}` : meta.title;

  const render = (r, key, type) => {
    const v = r[key];
    if (type === "time" && v) return new Date(v).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    if (type === "pill") return v ? <Chip tone={String(v).toLowerCase().includes("urgent") || String(v).toLowerCase().includes("critical") ? "red" : "blue"}>{v}</Chip> : "—";
    if (type === "ugx") return UGX(v || 0);
    if (type === "tab") return <span className="tabular text-xs">{v || "—"}</span>;
    return v || "—";
  };
  const sumUgx = isIcu ? 0 : (meta.cols || []).some((c) => c[2] === "ugx") ? (rows || []).reduce((s, r) => s + (r.amount || 0), 0) : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl border border-slate-200 max-w-4xl w-full max-h-[85vh] flex flex-col shadow-xl" data-testid={`admin-drill-${scope}`}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold text-slate-900">{title}</div>
          <button data-testid="admin-drill-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {rows === null ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState title={`Nothing to show for "${title}" yet.`} />
          ) : isIcu ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0"><tr><th className="text-left px-4 py-2">Bed</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Occupied by</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((b) => (
                  <tr key={b.bed_id}><td className="px-4 py-2 tabular">{b.bed_no}</td><td className="px-4 py-2"><Chip tone={b.status === "occupied" ? "red" : "green"}>{b.status}</Chip></td><td className="px-4 py-2">{b.patient_name || "—"}</td></tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-300 bg-white sticky bottom-0"><tr><td colSpan={3} className="px-4 py-2 text-right text-xs uppercase text-slate-500 font-semibold">Total occupied: {rows.filter((b)=>b.status==="occupied").length} of {rows.length}</td></tr></tfoot>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0"><tr>{meta.cols.map((c) => <th key={c[0]} className="text-left px-4 py-2">{c[0]}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => <tr key={r.id || r.user_id || r.queue_id || r.admission_id || r.payment_id || i}>{meta.cols.map((c) => <td key={c[0]} className="px-4 py-2">{render(r, c[1], c[2])}</td>)}</tr>)}
              </tbody>
              <tfoot className="border-t-2 border-slate-300 bg-white sticky bottom-0"><tr><td colSpan={meta.cols.length} className="px-4 py-2 text-right text-xs uppercase text-slate-500 font-semibold">{sumUgx != null ? `Total: ${UGX(sumUgx)}` : `${rows.length} row${rows.length===1?"":"s"}`}</td></tr></tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
